/* eslint-disable */
import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

// Material UI table
import { MaterialReactTable } from "material-react-table";
import { Box } from "@mui/material";

import { getData, putData } from "utils/Gateway";
import * as url from "helper/UrlHelper";

import TabsHeader from "components/Common/TabsHeader/TabsHeader";
import { commonHeaderLinkLists } from "helper/PatientManagementHelper/PatientManagementHelper";
import AccessDeniedView from "components/Common/AccessDeniedView/AccessDeniedView";
import AlertNotification from "components/Common/AlertNotification/AlertNotification";
import AllergiesHeader from "./Header/AllergiesHeader";
import {
  getDateFormatInSpanish,
  mergeListById,
} from "helper/Common/CommonHelper";
import LoadingSkeleton from "components/Common/ListLoadingSkeleton/LoadingSkeleton";
import { assetImages } from "constants";
import DeleteWarningModal from "components/Common/Modal/DeleteWarningModal";
import AssignUserModal from "components/Common/Modal/AssignUserModal";
import AllergyFilterPopup from "./Popup/AllergyFilterPopup";
import { removeTagHandler } from "helper/TagHelper/TagHelper";
import AddLabelModal from "components/Common/Modal/AddLabelModal";
import SaveAllergyPopup from "components/Common/Popup/SaveAllergyPopup";

const AllergiesListBody = () => {
  const { t } = useTranslation();
  const token = localStorage.getItem("token");
  const userInfo = JSON.parse(localStorage.getItem("userInfo"));
  const moduleAccess = localStorage.getItem("moduleaccess");

  const [isLoading, setIsLoading] = useState(false);
  const [allergyList, setAllergyList] = useState([]);
  const [rowSelection, setRowSelection] = useState({});
  const [selectedAllergyIds, setSelectedAllergyIds] = useState([]);
  const [selectedAllergyId, setSelectedAllergyId] = useState(null);
  const [selectedTagId, setSelectedTagId] = useState(null);
  const [isDataReady, setIsDataReady] = useState(false);

  // json filter query
  const [jsonFilterQuery, setJsonFilterQuery] = useState([]);
  const [isFilterReset, setIsFilterReset] = useState(false);
  const [reloadData, setReloadData] = useState(false);

  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [messageType, setMessageType] = useState("");

  // Get all allergies
  const getAllAllergies = async () => {
    try {
      setIsLoading(true);
      let requestUrl =
        url.API_BASE_URL + url.API_GET_ALL_ALLERGY + `?token=${token}`;

      if (jsonFilterQuery.length > 0) {
        // Sanitize the jsonFilterQuery
        const sanitizedQuery = jsonFilterQuery.map((filter) => {
          if (
            (filter.operator === "includes" ||
              filter.operator === "excludes") &&
            Array.isArray(filter.value)
          ) {
            return {
              ...filter,
              value: filter.value.map((item) => item.value).join(","), // Extract and join values as a comma-separated string
            };
          }
          return filter;
        });

        requestUrl += `&filters=${JSON.stringify(sanitizedQuery)}`;
      }

      const response = await getData(requestUrl);
      setIsLoading(false);
      if (response.status) {
        setAllergyList(response.data);
      }
    } catch (error) {
      setIsLoading(false);
    }
  };

  // Bulk update allergies
  const updateBulkAllergyHandler = async () => {
    if (selectedAllergyIds.length > 0) {
      try {
        const allergyListToBeUpdate = allergyList
          .filter((item) => selectedAllergyIds.includes(item._id))
          .map((item) => ({
            _id: item._id,
            allergen: item.allergen,
            allergytype: item.allergytype,
            reportedon: item.reportedon,
            resolvedon: item.resolvedon,
            severity: item.severity,
            reaction: item.reaction,
            currentstatus: item.currentstatus,
          }));
        let allergyData = { allergylist: allergyListToBeUpdate };
        let requestURL =
          url.API_BASE_URL + url.API_UPDATE_BULK_ALLERGY + `?token=${token}`;
        const response = await putData(requestURL, allergyData);
        if (response.status) {
          setMessageType("success");
          setTimeout(() => {
            setSelectedAllergyIds([]);
            setRowSelection({});
          }, 1500);
        } else {
          setMessageType("error");
        }
        setAlertMessage(response.message);
      } catch (error) {
        setAlertMessage(error.message);
        setMessageType("error");
      }
    } else {
      setAlertMessage("Please select at least one allergy");
      setMessageType("error");
    }
    setShowAlert(true);
  };

  // Bulk delete allergies
  const deleteBulkAllergyHandler = async () => {
    if (selectedAllergyIds.length > 0) {
      try {
        const allergyListToBeDelete = allergyList.filter((item) =>
          selectedAllergyIds.includes(item._id)
        );
        let allergyData = { allergylist: allergyListToBeDelete };
        let requestURL =
          url.API_BASE_URL + url.API_DELETE_BULK_ALLERGY + `?token=${token}`;
        const response = await putData(requestURL, allergyData);
        if (response.status) {
          setMessageType("success");
          setTimeout(() => {
            setAllergyList((prevList) =>
              prevList.filter((item) => !selectedAllergyIds.includes(item._id))
            );
            setSelectedAllergyIds([]);
            setRowSelection({});
          }, 1500);
        } else {
          setMessageType("error");
        }
        setAlertMessage(response.message);
      } catch (error) {
        setAlertMessage(error.message);
        setMessageType("error");
      }
    } else {
      setAlertMessage("Please select at least one allergy");
      setMessageType("error");
    }
    setShowAlert(true);
  };

  //remove tag from allergy
  const removeTagFromAllergy = async (tagIds, componentId) => {
    try {
      const componentUrl = url.API_ADD_REMOVE_TAG_ALLERGY;
      const data = await removeTagHandler(tagIds, componentId, componentUrl);

      if (data) {
        afterActionClose(data);
      }
    } catch (error) {
      console.log(error.message);
    } finally {
      setTimeout(() => {
        setSelectedTagId(null);
        setSelectedAllergyId(null);
      }, 1000);
    }
  };

  // After any action
  const afterActionClose = (data = []) => {
    setAllergyList((prevList) => mergeListById(prevList, data));
  };

  const resetFilterData = () => {
    setJsonFilterQuery([]);
    setIsFilterReset(true);
    setReloadData(true);
  };

  useEffect(() => {
    if (jsonFilterQuery.length > 0) {
      getAllAllergies();
      setIsDataReady(true);
    }
  }, [jsonFilterQuery]);

  useEffect(() => {
    if (reloadData) {
      getAllAllergies();
      setReloadData(false);
      setIsDataReady(true);
    }
  }, [reloadData]);

  // useEffect(() => {
  //   getAllAllergies();
  // }, []);

  const columns = useMemo(
    () => [
      {
        accessorKey: "#",
        header: t("Actions"),
        Cell: ({ row }) => (
          <button
            className="action_btn_mui"
            data-bs-toggle="offcanvas"
            data-bs-target="#saveAllergyOffCanvas"
            aria-controls="saveAllergyOffCanvas"
            onClick={() => {
              setSelectedAllergyId(row.original._id);
            }}
          >
            <span className="d-block material-symbols-outlined horz_icon">
              more_horiz
            </span>
          </button>
        ),
        enableColumnActions: false,
        enableColumnDragging: false,
        enableSorting: false,
      },
      {
        header: "Allergen",
        accessorKey: "allergen",
        size: 200,
        Cell: ({ row }) => (
          <span>
            {row.original.allergen ? (
              row.original.allergen
            ) : (
              <span style={{ color: "#bbb" }}></span>
            )}
          </span>
        ),
      },
      //tags column
      {
        accessorKey: "tagstring",
        header: t("Tags"),
        size: 200,
        Cell: ({ row }) => (
          <div className="label-wrapper py-1">
            <div className="d-flex gap-2">
              <div
                className={
                  row.original.taglist.length > 0
                    ? "label-container flex-grow-1"
                    : "d-none"
                }
              >
                <ul
                  className="d-flex flex-wrap gap-2 m-0 p-0"
                  style={{ listStyle: "none" }}
                >
                  {row.original.taglist.map((tag, index) => (
                    <li key={index} className="d-inline-flex">
                      <div className="badge d-flex align-items-center px-3 py-2 border-0 gradient-light text-black rounded-pill">
                        <span className="badge-text fs-xs fw-medium me-1">
                          {tag.title}
                        </span>
                        <Link
                          to="#"
                          className={`${
                            tag.moderator.toString() ===
                              userInfo._id.toString() ||
                            userInfo.role.slug === "ADMIN" ||
                            userInfo.role.slug === "SUPER_ADMIN"
                              ? "d-block"
                              : "d-none"
                          }`}
                          onClick={() => {
                            setSelectedAllergyId(row.original._id);
                            setSelectedTagId(tag._id);
                            removeTagFromAllergy([tag._id], row.original._id);
                          }}
                        >
                          {selectedTagId &&
                          selectedTagId === tag._id &&
                          selectedAllergyId &&
                          selectedAllergyId === row.original._id ? (
                            <div
                              className="spinner-border spinner-border-sm text-primary ms-1"
                              role="status"
                              style={{ height: "12px", width: "12px" }}
                            >
                              <span className="visually-hidden">
                                Loading...
                              </span>
                            </div>
                          ) : (
                            <span
                              className="d-block text-primary material-symbols-outlined icon-sm ms-1"
                              style={{ cursor: "pointer" }}
                            >
                              cancel
                            </span>
                          )}
                        </Link>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
              <button
                className="btn btn-light btn-sm rounded-circle p-1 shadow-sm border-0 d-flex align-items-center justify-content-center mt-1"
                style={{ width: "24px", height: "24px" }}
                data-bs-toggle="modal"
                data-bs-target="#addLabelModal"
                onClick={() => setSelectedAllergyIds([row.original._id])}
              >
                <span
                  className="material-symbols-outlined text-primary"
                  style={{ fontSize: "16px" }}
                >
                  add
                </span>
              </button>
            </div>
          </div>
        ),
      },
      {
        header: "Allergy Type",
        accessorKey: "allergytype",
        size: 180,
        Cell: ({ row }) => (
          <span>
            {row.original.allergytype ? (
              row.original.allergytype
            ) : (
              <span style={{ color: "#bbb" }}></span>
            )}
          </span>
        ),
      },
      {
        header: "Reported Date",
        accessorKey: "reportedondate",
        size: 160,
        Cell: ({ row }) => (
          <div className="joined border-bottom-0">
            <p className="d-flex align-items-center gap-2">
              <span className="d-block material-symbols-outlined icon-md">
                calendar_month
              </span>
              <span className="d-block">{row.original.reportedondate}</span>
            </p>
          </div>
        ),
      },
      {
        header: "Resolved Date",
        accessorKey: "resolvedondate",
        size: 160,
        Cell: ({ row }) => (
          <div className="joined border-bottom-0">
            <p className="d-flex align-items-center gap-2">
              <span className="d-block material-symbols-outlined icon-md">
                calendar_month
              </span>
              <span className="d-block">{row.original.resolvedondate}</span>
            </p>
          </div>
        ),
      },
      {
        header: "Severity",
        accessorKey: "severity",
        size: 120,
        Cell: ({ row }) => (
          <span>
            {row.original.severity ? (
              row.original.severity
            ) : (
              <span style={{ color: "#bbb" }}></span>
            )}
          </span>
        ),
      },
      {
        header: "Reaction",
        accessorKey: "reaction",
        size: 120,
        Cell: ({ row }) => (
          <span>
            {row.original.reaction ? (
              row.original.reaction
            ) : (
              <span style={{ color: "#bbb" }}></span>
            )}
          </span>
        ),
      },
      {
        header: "Current Status",
        accessorKey: "currentstatus",
        Cell: ({ row }) => (
          <span>
            {typeof row.original.currentstatus === "string" &&
            row.original.currentstatus !== "" ? (
              row.original.currentstatus
            ) : (
              <span style={{ color: "#bbb" }}></span>
            )}
          </span>
        ),
      },
      {
        header: "Assigned To",
        accessorKey: "assignedpersonname",
        Cell: ({ row }) => (
          <div
            className={
              row.original.assignedpersonname === ""
                ? "d-none"
                : "owner border-bottom-0 d-flex align-items-center gap-1"
            }
          >
            <img
              className="rounded-circle"
              height={35}
              width={35}
              src={
                row.original.assignedpersonimage == ""
                  ? assetImages.defaultUser
                  : url.SERVER_URL + row.original.assignedpersonimage
              }
              alt="User"
            />
            <p>
              <span className="d-block">{row.original.assignedpersonname}</span>
            </p>
          </div>
        ),
      },
    ],
    [selectedTagId, selectedAllergyId, selectedAllergyIds]
  );

  const columnOrder = [
    "#",
    "mrt-row-select",
    ...columns.map((c) => c.accessorKey),
  ];

  useEffect(() => {
    const selectedIdsArray = Object.keys(rowSelection).filter(
      (key) => rowSelection[key]
    );
    setSelectedAllergyIds(selectedIdsArray);
  }, [rowSelection]);

  const onAlertClose = () => {
    setShowAlert(false);
    setAlertMessage("");
    setMessageType("");
  };

  if (
    userInfo.role.slug === "ADMIN" ||
    userInfo.role.slug === "SUPER_ADMIN" ||
    (moduleAccess.includes("MOD_PATIENT_MANAGEMENT") &&
      moduleAccess.includes("MOD_PATIENT_ALLERGY"))
  ) {
    return (
      <div id="content_wrapper">
        <section className="crm-wrapper bg-white pb-5">
          <TabsHeader
            commonHeaderObject={commonHeaderLinkLists(t)}
            activeOption={t("Allergies")}
          />
          <div className="container-fluid px-lg-5">
            <AllergiesHeader
              reloadList={resetFilterData}
              selectedItemIds={selectedAllergyIds}
              updateBulkHandler={updateBulkAllergyHandler}
              jsonFilterQuery={jsonFilterQuery}
            />
            {isLoading || !isDataReady ? (
              <LoadingSkeleton />
            ) : (
              <div className="table-wrapper">
                <MaterialReactTable
                  columns={columns}
                  data={allergyList}
                  enableGrouping
                  enableRowSelection
                  getRowId={(row) => row._id}
                  onRowSelectionChange={setRowSelection}
                  state={{ rowSelection, columnOrder }}
                  initialState={{ rowSelection, columnOrder }}
                  defaultColumn={{
                    minSize: 20,
                    maxSize: 300,
                    size: 50,
                  }}
                  muiTableContainerProps={{
                    sx: {
                      maxHeight: "60vh",
                    },
                  }}
                  enableStickyHeader
                />
              </div>
            )}
          </div>
          {/* popup and modals */}

          <SaveAllergyPopup
            selectedAllergyId={selectedAllergyId}
            setSelectedAllergyId={setSelectedAllergyId}
            afterPopupClose={afterActionClose}
            setShowAlert={setShowAlert}
            setAlertMessage={setAlertMessage}
            setMessageType={setMessageType}
          />

          {/* You can add SaveAllergyPopup and AssignUserModal here if needed */}
          <AssignUserModal
            moduleName="allergy"
            afterModalClose={afterActionClose}
            selectedIds={selectedAllergyIds}
            setShowAlert={setShowAlert}
            setAlertMessage={setAlertMessage}
            setMessageType={setMessageType}
            onModalClose={() => {
              setRowSelection({});
              setSelectedAllergyIds([]);
            }}
          />

          {/* ------- filter popup ------- */}
          <AllergyFilterPopup
            setJsonFilterQuery={setJsonFilterQuery}
            reloadList={resetFilterData}
            moduleSlug="allergieslist"
            isFilterReset={isFilterReset}
            setIsFilterReset={setIsFilterReset}
            columnOrder={columnOrder}
          />

          <AddLabelModal
            moduleName="allergy"
            selectedIds={selectedAllergyIds}
            afterTagModalClose={afterActionClose}
            setShowAlert={setShowAlert}
            setAlertMessage={setAlertMessage}
            setMessageType={setMessageType}
            onModalClose={() => {
              setRowSelection({});
              setSelectedAllergyIds([]);
            }}
          />

          <DeleteWarningModal
            onDelete={deleteBulkAllergyHandler}
            onCancelDelete={() => {
              setRowSelection({});
              setSelectedAllergyIds([]);
            }}
            moduleName={t("allergy")}
          />

          {showAlert && (
            <AlertNotification
              showAlert={showAlert}
              message={alertMessage}
              alertType={messageType}
              onClose={onAlertClose}
            />
          )}
        </section>
      </div>
    );
  } else {
    return <AccessDeniedView />;
  }
};

export default AllergiesListBody;
